import { DISTRACTORS, TABLETS, type DoseItem } from "./dose-config";

/**
 * Dose Dash rules: what a day looks like, what catching a slot is worth, and
 * how the curve tightens.
 *
 * Kept apart from the React and the WebGL so the curve can be reasoned about
 * and tested. The shape mirrors Kae Kente's kente-rules deliberately — same
 * ideas in the same places, so the third game is a known quantity.
 *
 * These numbers MIRROR the server's DOSE_DASH constants, which bound what a
 * submitted score can be. If the curve changes here and not there, the
 * failure mode is honest players being told their score does not match the
 * run.
 */

export const LIVES = 3;
/**
 * The bar for "that went well": two perfect days at three slots scores about
 * 173. Round it to 175 — clearing it, or beating your own best, earns praise
 * rather than a taunt.
 */
export const GOOD_SCORE = 175;

export const BEST_SCORE_KEY = "@DoseDashBestScore";

/** Scores stop growing here, so the ceiling stays finite. The DIFFICULTY does
 *  not stop — see OVERTIME_FROM_DAY. */
export const MAX_DAY = 40;

/** Where the second, slower squeeze begins: after this the day and the window
 *  keep shrinking until the game is unplayable, which is how an uncapped run
 *  ends. */
export const OVERTIME_FROM_DAY = 11;
/** Backstops, not difficulty settings: a day this short with a window this
 *  narrow cannot be played, and that is the end of the run. */
export const DAY_HARD_FLOOR_MS = 4_000;
export const WINDOW_HARD_FLOOR = 0.025;

export interface DayPlan {
  /** 1-based. */
  day: number;
  /** How long the day takes. */
  dayMs: number;
  /** Window width as a fraction of the day. */
  window: number;
  /** Where in the day each slot falls. */
  slotPoints: number[];
  /** How many things sit on the tray when a slot opens. */
  trayCount: number;
}

/**
 * The curve tightens three separate screws, one at a time rather than all at
 * once: the day gets shorter, the window gets narrower, and the tray gets
 * busier. Slots per day only grow twice, because a fourth and fifth slot
 * change the feel far more than a shorter day does.
 */
export function planDay(day: number): DayPlan {
  const capped = Math.min(day, MAX_DAY);
  const slots = capped < 3 ? 3 : capped < 6 ? 4 : 5;

  // Past the point where the first curve bottoms out, the day and the window
  // keep tightening — slower, but without a floor to hide behind. A run has
  // no cap, so it has to end by becoming impossible (Ken, 2026-09-04);
  // otherwise a strong player never fails and the board measures stamina.
  const overtime = Math.max(0, day - OVERTIME_FROM_DAY);

  return {
    day,
    // 15s to start (Ken — 18 dragged), tightening by 0.9s a day to 9s, then
    // by 0.25s a day towards a hard floor no one can play.
    dayMs: Math.max(
      DAY_HARD_FLOOR_MS,
      Math.max(9_000, 15_000 - (capped - 1) * 900) - overtime * 250,
    ),
    window: Math.max(
      WINDOW_HARD_FLOOR,
      Math.max(0.06, 0.12 - (capped - 1) * 0.006) - overtime * 0.002,
    ),
    slotPoints: spreadSlots(slots),
    trayCount: Math.min(6, 4 + Math.floor((capped - 1) / 2)),
  };
}

/**
 * Where the slots fall. Deliberately uneven — evenly spaced slots are a
 * metronome and stop testing anything — and always inside the day with room
 * for the window at either end.
 */
function spreadSlots(count: number): number[] {
  const layouts: Record<number, number[]> = {
    3: [0.18, 0.52, 0.83],
    4: [0.14, 0.38, 0.63, 0.87],
    5: [0.12, 0.31, 0.5, 0.71, 0.89],
  };
  return layouts[count] ?? layouts[3];
}

/**
 * What one caught slot is worth. Later days pay more, and catching it early
 * in its window pays more than scraping it — so a confident player scores
 * above a lucky one on the same day.
 *
 * @param promptness 1 when caught the instant it opened, 0 at the very end.
 */
export function slotScore(day: number, promptness: number): number {
  const clamped = Math.min(1, Math.max(0, promptness));
  const capped = Math.min(day, MAX_DAY);
  return 10 + capped * 2 + Math.round(clamped * 10);
}

/** Paid for a day with nothing missed and nothing grabbed by mistake. */
export function perfectDayBonus(day: number): number {
  return 25 + Math.min(day, MAX_DAY) * 5;
}

/** Today's tablet, and the tray for one slot: today's tablet, a lookalike,
 *  and everyday things — shuffled, so neither the item nor its position can
 *  become muscle memory. */
export function buildTrayForDay(
  todaysTablet: DoseItem,
  trayCount: number,
  random: () => number = Math.random,
): DoseItem[] {
  const decoys = shuffle(
    TABLETS.filter((tablet) => tablet.key !== todaysTablet.key),
    random,
  );
  const others = shuffle(DISTRACTORS, random);

  const tray: DoseItem[] = [todaysTablet];
  // One lookalike tablet is the memory test; the rest of the tray is the
  // inhibition test, so extra slots go to everyday items.
  if (trayCount > 1) tray.push(decoys[0]);
  while (tray.length < trayCount && others.length > 0) {
    tray.push(others.shift() as DoseItem);
  }
  // A very busy tray needs the second lookalike too.
  if (tray.length < trayCount && decoys[1]) tray.push(decoys[1]);

  return shuffle(tray, random);
}

export function pickTablet(random: () => number = Math.random): DoseItem {
  return TABLETS[Math.floor(random() * TABLETS.length)];
}

/**
 * How many slots pass before the tablet changes (Ken, 2026-09-04). Holding
 * one target for a whole run is a memory test; having it swapped under you
 * every few slots is an UPDATING test, which is harder and much closer to
 * what a real schedule change feels like.
 *
 * The swap is always announced — an unannounced change would be a trick
 * rather than a difficulty.
 */
export const SLOTS_PER_TABLET_SWAP = 3;

export function shouldSwapTablet(slotsResolved: number): boolean {
  return slotsResolved > 0 && slotsResolved % SLOTS_PER_TABLET_SWAP === 0;
}

/** A different tablet from the one in play, so a "swap" always changes
 *  something. */
export function nextTablet(
  current: DoseItem,
  random: () => number = Math.random,
): DoseItem {
  const others = TABLETS.filter((tablet) => tablet.key !== current.key);
  return others[Math.floor(random() * others.length)];
}

function shuffle<T>(items: T[], random: () => number): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export function readBestScore(): number {
  try {
    const raw = localStorage.getItem(BEST_SCORE_KEY);
    const value = raw ? Number(raw) : 0;
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

export function writeBestScore(score: number): void {
  try {
    if (score > readBestScore()) {
      localStorage.setItem(BEST_SCORE_KEY, String(score));
    }
  } catch {
    // storage unavailable — the run still works, it just is not remembered
  }
}

/** End-of-run lines. Same rule as Kae Kente's: the joke is aimed at the
 *  play, never at anyone's health or family. */
export const TAUNTS = [
  "Chale, the day passed you!",
  "Massa, the sun no wait for you 😂",
  "You no serious!",
  "Ei! Ei! Ei! 🤣",
  "Chairman, w'akyi gu hɔ!",
  "W'asaaaa!",
];

export function pickTaunt(
  exclude?: string,
  random: () => number = Math.random,
): string {
  const pool =
    TAUNTS.length > 1 && exclude
      ? TAUNTS.filter((taunt) => taunt !== exclude)
      : TAUNTS;
  return pool[Math.floor(random() * pool.length)];
}
